/**
 * Renders one normalized MNIST sample as a data URL an SVG `<image>` can show.
 *
 * The graph draws the current training sample in the input block, so this module
 * is the inverse of `preprocess.ts`: it turns training-normalized pixels back
 * into byte grayscale.
 *
 * The same surface also shows a selected neuron's receptive field, so the digit
 * stays readable while the pixels it responds to are tinted over it.
 */
import { MNIST } from '../../shared/mnist/idx.ts';
import type { EdgePalette } from './edge-palette.ts';
import { focusStyle } from './edge-palette.ts';

function toByte(value: number): number {
  const byte = Math.round((value * MNIST.std + MNIST.mean) * 255);
  return Math.max(0, Math.min(255, byte));
}

type Rgb = [number, number, number];

/** Edge ramps return `rgb(...)`; magma returns hex. Both reach this module. */
function parseColor(color: string): Rgb {
  if (color.startsWith('#')) {
    const value = Number.parseInt(color.slice(1), 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  }
  const parts = color.slice(color.indexOf('(') + 1, -1).split(',');
  return [Number(parts[0] ?? 0), Number(parts[1] ?? 0), Number(parts[2] ?? 0)];
}

function createCanvas(): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = MNIST.cols;
  canvas.height = MNIST.rows;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('mnist-tile: 2D canvas context unavailable');
  }
  return { canvas, context };
}

export function mnistImageUrl(pixels: Float32Array): string {
  const { canvas, context } = createCanvas();
  const image = context.createImageData(MNIST.cols, MNIST.rows);
  for (let i = 0; i < MNIST.pixels; i += 1) {
    const byte = toByte(pixels[i] ?? 0);
    image.data[i * 4] = byte;
    image.data[i * 4 + 1] = byte;
    image.data[i * 4 + 2] = byte;
    image.data[i * 4 + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  return canvas.toDataURL();
}

/**
 * Per-palette cache of quantized sensitivity → blended pixel colour.
 *
 * The tile is rebuilt every couple of seconds, and a ramp call plus colour
 * parsing for each of the 784 pixels is worth avoiding. Quantizing to 1/128
 * keeps the cache small without a visible banding difference.
 */
const blends = new Map<EdgePalette, Map<number, Rgb>>();

function blendedColor(ink: number, sensitivity: number, palette: EdgePalette): Rgb {
  const quantized = Math.round(Math.max(-1, Math.min(1, sensitivity)) * 128);
  let cache = blends.get(palette);
  if (!cache) {
    cache = new Map();
    blends.set(palette, cache);
  }

  const cached = cache.get(quantized);
  if (cached) {
    return cached;
  }

  const style = focusStyle(quantized / 128, palette);
  const [red, green, blue] = parseColor(style.color);
  // Multiplicative, not additive: the field tints the ink and leaves the paper
  // dark. Adding the colour instead would paint the background as well, and with
  // 784 pixels in a 28x28 grid that turns an untrained neuron's noise into
  // static that hides the very digit the field has to be read against.
  const weight = style.opacity;
  const mix = (channel: number): number =>
    Math.max(0, Math.min(255, Math.round(ink * (1 - weight + weight * (channel / 255)))));
  const result: Rgb = [mix(red), mix(green), mix(blue)];
  cache.set(quantized, result);
  return result;
}

/**
 * The sample with a selected neuron's receptive field tinted over it.
 *
 * `sensitivity` holds one signed, max-normalized value per pixel. Hue carries the
 * sign on the active edge ramp, so excitatory and inhibitory regions are
 * distinguishable; magnitude is carried by opacity so the digit underneath still
 * reads.
 */
export function focusImageUrl(
  pixels: Float32Array,
  sensitivity: Float32Array,
  palette: EdgePalette,
): string {
  const { canvas, context } = createCanvas();
  const image = context.createImageData(MNIST.cols, MNIST.rows);
  for (let i = 0; i < MNIST.pixels; i += 1) {
    const [red, green, blue] = blendedColor(toByte(pixels[i] ?? 0), sensitivity[i] ?? 0, palette);
    image.data[i * 4] = red;
    image.data[i * 4 + 1] = green;
    image.data[i * 4 + 2] = blue;
    image.data[i * 4 + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  return canvas.toDataURL();
}
